// ============================================================================
// JARVISTRAVEL - PLAN INTENT (JAR-1184)
// Carries "this visitor chose Explore Annual" from the marketing pricing page,
// across signup, to the plan gate, where it arrives selected (JAR-3179).
// ============================================================================

import type { SubscriptionPlan } from '@/app/api/payments';

/**
 * The lookup keys marketing is allowed to name.
 *
 * This is deliberately a closed set rather than "whatever arrived in the URL".
 * The value ends up in a request body the server acts on, so an allowlist keeps
 * a hand-edited query string from reaching the API as a plan identifier. The
 * server refuses unknown and unoffered plans anyway (service.go:280) — this is
 * the near half of that same guard, not a replacement for it.
 *
 * Kept in sync with marketing's `PriceLookupKey` (marketing-website
 * src/app/data/pricing.ts) and with core's catalogue (config/stripe/setup.js).
 * planContract.test.ts pins the set's internal shape (format, uniqueness,
 * membership) so a local typo cannot slip in silently; the CROSS-REPO half of
 * the sync is enforced at review + marketing's CTA guard, not here — there is
 * no shared module for the three repos to import, and that gap is tracked as a
 * JAR-1184 follow-up rather than papered over with a copy.
 */
export const KNOWN_LOOKUP_KEYS = ['jt_trip_pass', 'jt_explore_annual', 'jt_explore_monthly'] as const;

export type PlanLookupKey = (typeof KNOWN_LOOKUP_KEYS)[number];

const STORAGE_KEY = 'jt.planIntent';

/**
 * How long a captured choice stays actionable.
 *
 * sessionStorage already dies with the tab, which covers "come back next week".
 * It does not cover a tab left open: a visitor captures a plan, wanders off,
 * and someone signs in on that tab hours later and finds a choice they never
 * made selected for them. Sign-out purges the key (localDataPurge sweeps the
 * `jt.` prefix), so the cross-user case is closed — this bounds the same-tab
 * one (JAR-1222).
 *
 * An hour is deliberately generous: the signup chain includes waiting for a
 * verification email, and expiring mid-flow would lose a choice the visitor is
 * actively acting on. It is short enough that an abandoned tab does not fire.
 */
const INTENT_TTL_MS = 60 * 60 * 1000;

/**
 * The choice when sessionStorage can't hold it (blocked site data, private
 * modes that throw). Kept for this page load, which is the whole sign-up chain
 * up to the plan gate, and cleared with the stored one.
 */
let unstored: { key: PlanLookupKey; at: number } | null = null;

/** The query parameter marketing appends to the app URL. */
export const PLAN_QUERY_PARAM = 'plan';

function isKnown(value: string | null | undefined): value is PlanLookupKey {
  return !!value && (KNOWN_LOOKUP_KEYS as readonly string[]).includes(value);
}

/**
 * sessionStorage is unavailable in more situations than people expect: Safari
 * private mode historically threw on write, and any browser set to block site
 * data throws on read. A plan intent is a convenience, never a correctness
 * requirement, so every access degrades to "no intent" rather than propagating.
 */
function safeGet(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** True when the value is actually remembered; callers must not act as if it were otherwise. */
function safeSet(value: string): boolean {
  try {
    // Stored WITH the capture time, as one JSON value. A separate timestamp key
    // could be written, lost or cleared independently of the key it dates, and
    // a plan with no time is a plan that never expires.
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ key: value, at: Date.now() }));
    return true;
  } catch {
    /* no intent is a supported state */
    return false;
  }
}

/**
 * Read `?plan=` off the current URL, remember it, and strip it from the address
 * bar.
 *
 * Why sessionStorage rather than keeping it in the URL: the post-signup chain is
 * /auth/sign-up → /auth/verify-email → /onboarding, and every hop uses
 * `navigate(..., { replace: true })`. A query parameter does not survive the
 * first replace. It also has to survive the round trip to Stripe's domain and
 * back, which no in-memory state does.
 *
 * Why sessionStorage rather than localStorage: the intent should die with the
 * tab. A visitor who abandons checkout in March should not find that plan
 * chosen for them when they open the app in June.
 *
 * Returns the captured key, or null if there was nothing valid to capture. When
 * sessionStorage can't hold it, it is kept for this page load instead, and the
 * parameter is left in the URL so a refresh can capture it again.
 */
// `search` is resolved INSIDE the try, not as a default parameter. A default
// parameter evaluates before the body runs, so in a window-less context (SSR,
// a runner that stubs only part of the browser globals) the old signature
// threw ReferenceError instead of degrading to "no intent" — the opposite of
// the safeGet/safeSet posture this file sets (web-app#252 review).
export function capturePlanIntent(search?: string): PlanLookupKey | null {
  let raw: string | null;
  try {
    const query = search ?? window.location.search;
    raw = new URLSearchParams(query).get(PLAN_QUERY_PARAM);
  } catch {
    return null;
  }
  if (!isKnown(raw)) return null;

  // Strip the parameter ONLY once the choice is safely remembered. A write
  // that fails (Safari private mode, blocked site data) was swallowed while the
  // parameter was stripped regardless, which destroyed the last record of the
  // choice; nothing downstream reads this function's return value, so the loss
  // was silent (OCR LOW, web-app#252). Left in the URL, a refresh re-captures.
  if (!safeSet(raw)) {
    unstored = { key: raw, at: Date.now() };
    return raw;
  }
  // The stored choice replaces one kept in memory earlier in this page load,
  // so an older choice can't come back once the stored one is gone.
  unstored = null;

  // Strip the parameter so a refresh, a shared link or a back navigation does
  // not re-arm an intent the visitor already acted on. Guarded because
  // replaceState throws in a sandboxed iframe and is absent in test environments
  // that stub only part of History.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(PLAN_QUERY_PARAM);
    // Keep React Router's own history state (idx, key, usr) rather than wiping it
    // with {}: dropping idx made the router read the next entry's index as NaN,
    // so Back stopped closing open sheets in that tab (JAR-2504).
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* the address bar keeps the parameter; the intent is still captured */
  }

  return raw;
}

/**
 * The remembered choice, or null. Never returns a key outside the allowlist,
 * and never one older than INTENT_TTL_MS.
 *
 * A MISSING timestamp counts as expired, not as fresh. That is the direction
 * that fails safe: an envelope this function cannot date — a value written by
 * an older build, a hand-edited key, a partial write — is exactly the value
 * whose age is unknown, and treating unknown as "fine" is how a guard gets
 * bypassed by absence rather than by a wrong value (JAR-1222).
 *
 * Expiry CLEARS as well as refusing, so a stale intent cannot sit in storage
 * being re-read and re-refused by every caller for the life of the tab.
 */
/**
 * Remembers a choice made IN the app rather than one carried in by a link
 * (JAR-3179 review). The gate's own selection is a choice too: without this, a
 * person who picked Trip Pass and left after a failed checkout came back to
 * Explore Annual, and Continue would have bought it.
 *
 * Same envelope as the link's, so everything downstream — the TTL, the
 * allowlist, sign-out purge, CheckoutReturn's clear — treats the two alike.
 * Returns false when nothing could be stored, which callers may ignore: the
 * remembered choice is a convenience, not a gate.
 */
export function savePlanIntent(key: string): boolean {
  if (!isKnown(key)) return false;
  if (!safeSet(key)) {
    unstored = { key, at: Date.now() };
    return false;
  }
  unstored = null;
  return true;
}

export function readPlanIntent(): PlanLookupKey | null {
  const stored = safeGet();
  if (!stored) {
    if (!unstored) return null;
    if (Date.now() - unstored.at > INTENT_TTL_MS) return expire();
    return unstored.key;
  }

  let key: unknown;
  let at: unknown;
  try {
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return expire();
    ({ key, at } = parsed as { key?: unknown; at?: unknown });
  } catch {
    // Not JSON at all — a bare key from a build before this envelope existed.
    // Undateable, therefore expired.
    return expire();
  }

  if (typeof at !== 'number' || !Number.isFinite(at)) return expire();
  if (Date.now() - at > INTENT_TTL_MS) return expire();
  if (typeof key !== 'string' || !isKnown(key)) return expire();
  return key;
}

/** Drop an unusable intent and report none, so it is refused exactly once. */
function expire(): null {
  clearPlanIntent();
  return null;
}

/** Forget the choice. Call once it has been acted on, or once it cannot be. */
export function clearPlanIntent(): void {
  unstored = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Find the plan a lookup key names.
 *
 * Both guards below defend one specific collision: the server tags
 * stripe_lookup_key `omitempty` (core models.go:18), so a plan with an empty key
 * serialises with no field at all and arrives as `undefined`. A bare
 * `plan.stripe_lookup_key === lookupKey` would then match `undefined` against
 * `undefined` and select a plan the visitor never chose, which surfaces as a
 * charge for the wrong thing.
 *
 * Being precise about what protects that, because an earlier version of this
 * comment claimed more than it could show: the TYPES rule it out. `lookupKey` is
 * `PlanLookupKey | null`, so `undefined` cannot arrive through a checked caller,
 * and removing both guards leaves every test green and the build clean — which
 * is exactly how it was measured.
 *
 * They stay because the types are erased at runtime and this data is parsed from
 * a network response: `listPlans()` hands back whatever the server actually
 * sent. A truncated or reshaped payload is the one caller TypeScript cannot
 * check, and it is the caller that would pick the wrong plan. The test covering
 * this casts deliberately for that reason.
 */
export function findPlanByLookupKey(
  plans: readonly SubscriptionPlan[],
  lookupKey: PlanLookupKey | null,
): SubscriptionPlan | null {
  // The list is guarded like the key is. This is an exported utility whose own
  // comments justify defending against reshaped payloads, and a caller handing
  // it null or a non-array got `plans.find is not a function` instead of null
  // (web-app#252 review).
  if (!Array.isArray(plans)) return null;
  if (!lookupKey) return null;
  // `!!plan` for the same reason as Array.isArray above: a list parsed off the
  // wire can carry a null element, and that threw before the comparison ran.
  return plans.find((plan) => !!plan && !!plan.stripe_lookup_key && plan.stripe_lookup_key === lookupKey) ?? null;
}
